import React from "react";
import { ISelectOption } from "../../constants/types";
import { api } from "../../api/api";
import { CustomFormLabel, CustomTextField } from "../formsComponents";
import { Autocomplete, CircularProgress } from "@mui/material";
import { IUsersAutoSearchByRole } from "./AutoCompleteSearches.types";
import { IUserCreation } from "../../redux/UserProfileList/userProfile.types";
import { useBillingCompanyContext } from "../../providers";
import { debounce } from "lodash";

export const UsersAutoSearchByRole: React.FC<IUsersAutoSearchByRole> = (
  props
) => {
  const {
    roleId,
    value,
    label,
    onChange,
    showTopLabel = false,
    isHighlighted = false,
    placeholder = "",
  } = props;

  const [usersList, setUsersList] = React.useState<ISelectOption[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>("");
  const companyOutlet = useBillingCompanyContext();
  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;

  const fetchUsers = React.useCallback(
    async (enteredValue: string) => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (roleId) {
          params.append("role_id", String(roleId));
        }
        if (enteredValue) {
          params.append("columns", "first_name,last_name");
          params.append("value", enteredValue);
        }

        const query = params.toString();
        const res = await api.get(`/user/get-user${query ? `?${query}` : ""}`);
        const data: IUserCreation[] = res.data?.data || [];
        const finalData: ISelectOption[] = [];
        for (const user of data) {
          if (user.user_uuid) {
            const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
            finalData.push({
              label: name || user.first_name || "",
              value: user.user_uuid,
            });
          }
        }

        if (value) {
          const selectedValue =
            typeof value === "object" ? (value as any)?.user_uuid : value;
          if (selectedValue) {
            const exists = finalData.some(
              (opt) => String(opt.value) === String(selectedValue)
            );
            if (!exists) {
              setUsersList((prevList) => {
                const existingOpt = prevList.find(
                  (opt) => String(opt.value) === String(selectedValue)
                );
                return existingOpt ? [existingOpt, ...finalData] : finalData;
              });
              return;
            }
          }
        }

        setUsersList(finalData);
      } catch (error) {
        console.error("Error fetching users by role:", error);
      } finally {
        setLoading(false);
      }
    },
    [roleId, value]
  );

  const debouncedFetchUsers = React.useMemo(
    () =>
      debounce((text: string) => {
        fetchUsers(text);
      }, 400),
    [fetchUsers]
  );

  React.useEffect(() => {
    debouncedFetchUsers(searchText);
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [searchText, debouncedFetchUsers]);

  const getOptionLabel = (option: string | ISelectOption) => {
    if (typeof option === "string") {
      return option;
    }
    return option.label || "";
  };

  return (
    <>
      {showTopLabel && label && <CustomFormLabel>{label}</CustomFormLabel>}
      <Autocomplete
        options={usersList}
        loading={loading}
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "2px",
            paddingBottom: "2px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
          },
          ...(isHighlighted && {
            backgroundColor: "#fff2c7",
            borderRadius: 1,
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: "warning.main",
              borderWidth: "2px !important",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "warning.main",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "warning.main",
            },
          }),
        }}
        value={
          usersList.find(
            (option) =>
              String(option.value) === String(value) ||
              (typeof value === "object" &&
                (value as any)?.user_uuid &&
                String(option.value) === String((value as any).user_uuid))
          ) || null
        }
        isOptionEqualToValue={(option, val) =>
          String(option.value) ===
          String(typeof val === "object" ? (val as any)?.value : val)
        }
        getOptionLabel={getOptionLabel}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            setSearchText(newInputValue);
          }
        }}
        onChange={(e, newValue) => {
          if (newValue && typeof newValue !== "string") {
            onChange(newValue.value, newValue);
          } else {
            onChange("", null);
          }
        }}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={!showTopLabel ? label : undefined}
            placeholder={placeholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink:
                (params.InputLabelProps as { shrink?: boolean })?.shrink ||
                Boolean(params.inputProps.value) ||
                Boolean(placeholder),
            }}
          />
        )}
      />
    </>
  );
};
